import { useCallback, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Conversation, Message } from '../domain/conversations';
import { useInterval } from '../hooks/useInterval';
import { useLoad } from '../hooks/useLoad';
import { useOutbox } from '../hooks/useOutbox';
import { useRepositories } from '../hooks/useRepositories';
import { useMyId } from '../hooks/useSession';
import MessageComposer from '../components/messaging/MessageComposer';
import MessageThread from '../components/messaging/MessageThread';
import ReadOnlyNotice from '../components/messaging/ReadOnlyNotice';
import { EmptyState, LoadingState, StateForError } from '../components/PageStates';
import UserAvatar from '../components/UserAvatar';
import { conversationTitle } from '../utils/conversations';
import { ArrowLeft, MessageCircle, Package } from 'lucide-react';

/** How often an open conversation looks for new messages (main's interval). */
export const CONVERSATION_REFRESH_MS = 10_000;

/**
 * One conversation, by its own id (never a participant's): the messages I
 * may read, and the message box while I can send — else the read-only
 * notice (018 FR-007). Every load marks it read; it refreshes quietly while
 * open.
 */
export default function ConversationDetailPage() {
  const { conversationId = '' } = useParams<{ conversationId: string }>();
  const { conversations } = useRepositories();
  const conversation = useLoad(async () => {
    const c = await conversations.get(conversationId);
    conversations.markRead(conversationId).catch(() => {
      // Unread comes back at the next load; nothing to show for it.
    });
    return c;
  }, conversationId);
  useInterval(conversation.refresh, CONVERSATION_REFRESH_MS, conversation.status === 'ready');

  if (conversation.status === 'loading') return <LoadingState label="Loading conversation" />;
  if (conversation.status === 'error') {
    return (
      <StateForError error={conversation.error} onRetry={conversation.reload} what="Messages" />
    );
  }
  return (
    <ConversationView
      key={conversationId}
      conversation={conversation.data}
      onChanged={conversation.refresh}
      onBlocked={conversation.reload}
    />
  );
}

function ConversationView({
  conversation,
  onChanged,
  onBlocked,
}: {
  conversation: Conversation;
  onChanged: () => void;
  onBlocked: () => void;
}) {
  const { conversations } = useRepositories();
  const myId = useMyId();
  const outbox = useOutbox(conversation.id, onChanged);
  const shownIds = useMemo(
    () => new Set(conversation.messages.map((m) => m.id)),
    [conversation.messages],
  );
  const { settle } = outbox;
  useEffect(() => settle(shownIds), [settle, shownIds]);

  const takeBack = useCallback(
    async (m: Message) => {
      await conversations.takeBack(m.id);
      onChanged();
    },
    [conversations, onChanged],
  );

  const first = conversation.otherPeople[0];
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <div>
        <Link
          to="/conversations"
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="size-4" />
          Messages
        </Link>
        <div className="flex items-center gap-3">
          {first && <UserAvatar person={first} className="size-12" textClassName="text-base" />}
          <div className="min-w-0">
            <h1 className="truncate text-3xl font-bold tracking-tight text-foreground">
              {conversationTitle(conversation)}
            </h1>
            {conversation.listing && (
              <Link
                to={`/listings/${conversation.listing.id}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
              >
                <Package className="size-4" aria-hidden="true" />
                About {conversation.listing.title}
              </Link>
            )}
          </div>
        </div>
      </div>

      {conversation.messages.length === 0 && outbox.outgoing.length === 0 ? (
        <EmptyState
          icon={<MessageCircle className="size-8" />}
          title="No messages yet"
          description="Say hello."
        />
      ) : (
        <MessageThread
          messages={conversation.messages.filter(
            (m) => !outbox.outgoing.some((o) => o.sentId === m.id),
          )}
          outgoing={outbox.outgoing}
          myId={myId}
          isGroup={conversation.kind === 'group'}
          onTakeBack={takeBack}
          onRetry={outbox.retry}
          onBlocked={onBlocked}
        />
      )}

      {conversation.canSend ? <MessageComposer onSend={outbox.send} /> : <ReadOnlyNotice />}
    </div>
  );
}
